<template>
  <div v-if="camera">
    <div v-if="topicValue" class="image_container">
      <router-link :to="{path: '/cameras/' + camera.id}">
        <img :src="urlSnapshot" @load="loadHandler" @error="errorHandler" :style="imageStyle" class="image"/>
      </router-link>
    </div>
    <div v-else>No snapshot path provided.</div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex';
  import {randomInteger, cameraSnapshotUrl} from "../../utils/utils";

  // Multiplier applied to the refresh delay while the camera is failing.
  const failureBackoff = 10;
  // If the browser reports neither load nor error within this many times the refresh
  // delay, treat the request as hung and move on.
  const stallFactor = 4;

  export default {
    name: "camera-viewer",
    props: ['camera'],
    data() {
      return {
        refreshTimer: undefined,
        stallTimer: undefined,
        observer: undefined,
        nonce: 0,
        secondsBetweenFetches: 2,
        isFailed: false,
        isOnScreen: true,
        isPageVisible: true
      };
    },
    computed: {
      ...mapGetters('mqtt', ['valueForTopic']),
      ...mapGetters('ui', ['windowSizes', 'cameraSmallViewSnapshotInterval']),
      topicValue() {
        return this.topicValueByName('snapshotUrl');
      },
      snapshotInterval() {
        const published = parseFloat(this.topicValueByName('snapshotInterval'));
        const base = published > 0 ? published : this.secondsBetweenFetches;

        return this.isFailed ? base * failureBackoff : base;
      },
      // Polling is pointless while the tile is scrolled out of view or the tab is in the
      // background: it costs a request per tick against the camera for nothing.
      isActive() {
        return this.isOnScreen && this.isPageVisible;
      },
      urlSnapshot() {
        return cameraSnapshotUrl(this.camera.id, this.nonce);
      },
      imageWidth() {
        const w = this.windowSizes['width'];
        if (w >= 768) {
          return 320;
        } else if (w >= 640) {
          return 320;
        } else if (w >= 500) {
          return 480;
        } else if (w >= 425) {
          return 320;
        } else if (w >= 375) {
          return 280;
        }

        return 240;
      },
      imageHeight() {
        return this.windowSizes['height'];
      },
      imageStyle() {
        return `width: ${this.imageWidth}px;`;
      }
    },
    watch: {
      isActive(active) {
        if (active) {
          this.requestFrame();
        } else {
          this.clearTimers();
        }
      }
    },
    methods: {
      topicValueByName(name) {
        if (!this.camera || !this.camera.topics) {
          return null;
        }

        const topic = this.camera.topics.find(item => item.name === name);

        return topic ? this.valueForTopic(topic.topic) : null;
      },
      clearTimers() {
        clearTimeout(this.refreshTimer);
        clearTimeout(this.stallTimer);
        this.refreshTimer = undefined;
        this.stallTimer = undefined;
      },
      // Self-scheduling loop: the next frame is only ever requested once the current one
      // settled. It cannot pile up requests, it always reschedules (so a failure on the
      // very first load still recovers), and it re-reads the interval on every tick.
      scheduleNext() {
        this.clearTimers();

        if (!this.isActive) {
          return;
        }

        this.refreshTimer = setTimeout(() => {
          this.requestFrame();
        }, this.snapshotInterval * 1000);
      },
      requestFrame() {
        this.clearTimers();

        if (!this.isActive) {
          return;
        }

        this.nonce++;

        this.stallTimer = setTimeout(() => {
          this.isFailed = true;
          this.scheduleNext();
        }, this.snapshotInterval * stallFactor * 1000);
      },
      loadHandler() {
        this.isFailed = false;
        this.scheduleNext();
      },
      errorHandler() {
        this.isFailed = true;
        this.scheduleNext();
      },
      visibilityHandler() {
        this.isPageVisible = !document.hidden;
      },
      observeVisibility() {
        if (typeof IntersectionObserver === 'undefined' || !(this.$el instanceof Element)) {
          return;
        }

        this.observer = new IntersectionObserver(entries => {
          this.isOnScreen = entries.some(entry => entry.isIntersecting);
        });
        this.observer.observe(this.$el);
      }
    },
    mounted() {
      // Spread the tiles out a little so a board full of cameras does not fire every
      // request on the same tick.
      this.secondsBetweenFetches = this.cameraSmallViewSnapshotInterval + randomInteger(0, 9) / 100;
      this.isPageVisible = !document.hidden;

      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.observeVisibility();

      // The first frame is already being fetched by the initial render; guard it against
      // hanging so the loop starts even if that request never settles.
      this.stallTimer = setTimeout(() => {
        this.isFailed = true;
        this.scheduleNext();
      }, this.snapshotInterval * stallFactor * 1000);
    },
    destroyed() {
      this.clearTimers();

      document.removeEventListener('visibilitychange', this.visibilityHandler);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = undefined;
      }
    }
  }
</script>

<style scoped>
  .image_container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image {
    border: solid 1px #000000;
  }
</style>
